import type { Rect } from './geometry';

export const DEFAULT_TILE_SIZE = 256;

export interface TileCoordinate {
  x: number;
  y: number;
}

export interface TileEntry extends TileCoordinate {
  pixels: Uint8ClampedArray;
}

export class RasterTileStore {
  readonly tiles = new Map<string, Uint8ClampedArray>();
  private readonly revisions = new Map<string, number>();
  private revision = 0;

  constructor(
    readonly width: number,
    readonly height: number,
    readonly tileSize = DEFAULT_TILE_SIZE,
  ) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
      throw new Error('Raster dimensions must be positive integers.');
    }
  }

  static key(x: number, y: number): string {
    return `${x}:${y}`;
  }

  tileDimensions(x: number, y: number): { width: number; height: number } {
    return {
      width: Math.min(this.tileSize, this.width - x * this.tileSize),
      height: Math.min(this.tileSize, this.height - y * this.tileSize),
    };
  }

  getTile(x: number, y: number, create = false): Uint8ClampedArray | undefined {
    const key = RasterTileStore.key(x, y);
    let pixels = this.tiles.get(key);
    if (!pixels && create) {
      const dimensions = this.tileDimensions(x, y);
      if (dimensions.width <= 0 || dimensions.height <= 0) return undefined;
      pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
      this.tiles.set(key, pixels);
      this.touchTile(x, y);
    }
    return pixels;
  }

  setTile(x: number, y: number, pixels: Uint8ClampedArray | null): void {
    const key = RasterTileStore.key(x, y);
    if (!pixels) {
      this.tiles.delete(key);
      this.touchTile(x, y);
      return;
    }
    const dimensions = this.tileDimensions(x, y);
    const expected = dimensions.width * dimensions.height * 4;
    if (pixels.length !== expected) throw new Error(`Invalid tile byte length: expected ${expected}, received ${pixels.length}.`);
    this.tiles.set(key, new Uint8ClampedArray(pixels));
    this.touchTile(x, y);
  }

  touchTile(x: number, y: number): void {
    this.revision += 1;
    this.revisions.set(RasterTileStore.key(x, y), this.revision);
  }

  tileRevision(x: number, y: number): number {
    return this.revisions.get(RasterTileStore.key(x, y)) ?? 0;
  }

  storeRevision(): number { return this.revision; }

  entries(): TileEntry[] {
    const result: TileEntry[] = [];
    for (const [key, pixels] of this.tiles) {
      const [xText, yText] = key.split(':');
      const x = Number(xText);
      const y = Number(yText);
      if (Number.isInteger(x) && Number.isInteger(y)) result.push({ x, y, pixels });
    }
    return result;
  }

  visibleEntries(localBounds: Rect): TileEntry[] {
    const minX = Math.max(0, Math.floor(localBounds.x / this.tileSize));
    const minY = Math.max(0, Math.floor(localBounds.y / this.tileSize));
    const maxX = Math.min(Math.ceil(this.width / this.tileSize) - 1, Math.floor((localBounds.x + localBounds.width) / this.tileSize));
    const maxY = Math.min(Math.ceil(this.height / this.tileSize) - 1, Math.floor((localBounds.y + localBounds.height) / this.tileSize));
    const result: TileEntry[] = [];
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        const pixels = this.getTile(x, y);
        if (pixels) result.push({ x, y, pixels });
      }
    }
    return result;
  }

  byteLength(): number {
    let total = 0;
    for (const tile of this.tiles.values()) total += tile.byteLength;
    return total;
  }

  clone(): RasterTileStore {
    const clone = new RasterTileStore(this.width, this.height, this.tileSize);
    for (const entry of this.entries()) clone.setTile(entry.x, entry.y, entry.pixels);
    return clone;
  }

  static fromImageData(image: Pick<ImageData, 'width' | 'height' | 'data'>, tileSize = DEFAULT_TILE_SIZE): RasterTileStore {
    const store = new RasterTileStore(image.width, image.height, tileSize);
    const columns = Math.ceil(image.width / tileSize);
    const rows = Math.ceil(image.height / tileSize);
    for (let tileY = 0; tileY < rows; tileY += 1) {
      for (let tileX = 0; tileX < columns; tileX += 1) {
        const dimensions = store.tileDimensions(tileX, tileY);
        const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
        let nonTransparent = false;
        for (let y = 0; y < dimensions.height; y += 1) {
          const sourceStart = ((tileY * tileSize + y) * image.width + tileX * tileSize) * 4;
          const targetStart = y * dimensions.width * 4;
          const row = image.data.subarray(sourceStart, sourceStart + dimensions.width * 4);
          pixels.set(row, targetStart);
          for (let index = 3; index < row.length; index += 4) nonTransparent ||= (row[index] ?? 0) > 0;
        }
        if (nonTransparent) {
          store.tiles.set(RasterTileStore.key(tileX, tileY), pixels);
          store.touchTile(tileX, tileY);
        }
      }
    }
    return store;
  }

  toImageData(): ImageData {
    const image = new ImageData(this.width, this.height);
    for (const entry of this.entries()) {
      const dimensions = this.tileDimensions(entry.x, entry.y);
      for (let y = 0; y < dimensions.height; y += 1) {
        const sourceStart = y * dimensions.width * 4;
        const targetStart = ((entry.y * this.tileSize + y) * this.width + entry.x * this.tileSize) * 4;
        image.data.set(entry.pixels.subarray(sourceStart, sourceStart + dimensions.width * 4), targetStart);
      }
    }
    return image;
  }
}
